import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { IonIcon } from "@ionic/react";
import { chevronBackOutline, chevronForwardOutline } from "ionicons/icons";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";

import styles from "./SubmitterCalendarPopover.module.scss";

type Props = {
  selectedDate: string | null;
  markedDates: string[];
  onChangeViewMonth: (year: number, month: number) => void;
  onSelect: (date: string) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
};

const WEEK_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

const parseYmd = (ymd: string): Date => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const SubmitterCalendarPopover: FC<Props> = ({
  selectedDate,
  markedDates,
  onChangeViewMonth,
  onSelect,
  onClose,
  anchorRef,
}) => {
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    selectedDate ? parseYmd(selectedDate) : new Date(),
  );
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onChangeViewMonth(viewMonth.getFullYear(), viewMonth.getMonth() + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMonth]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (anchorRef?.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(viewMonth), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(viewMonth), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [viewMonth]);

  const markedSet = useMemo(() => new Set(markedDates), [markedDates]);
  const selected = selectedDate ? parseYmd(selectedDate) : null;

  return (
    <div ref={containerRef} className={styles.popover}>
      <div className={styles.header}>
        <button
          type="button"
          aria-label="前月"
          className={styles.navButton}
          onClick={() => setViewMonth(prev => subMonths(prev, 1))}
        >
          <IonIcon icon={chevronBackOutline} />
        </button>
        <span className={styles.monthLabel}>
          {format(viewMonth, "yyyy年M月")}
        </span>
        <button
          type="button"
          aria-label="次月"
          className={styles.navButton}
          onClick={() => setViewMonth(prev => addMonths(prev, 1))}
        >
          <IonIcon icon={chevronForwardOutline} />
        </button>
      </div>
      <div className={styles.weekHeader}>
        {WEEK_LABELS.map(label => (
          <span key={label}>{label}</span>
        ))}
      </div>
      <div className={styles.grid}>
        {days.map(day => {
          const ymd = format(day, "yyyy-MM-dd");
          const isCurrentMonth = isSameMonth(day, viewMonth);
          const isSelected = !!selected && isSameDay(day, selected);
          const isMarked = markedSet.has(ymd);
          const dayIsToday = isToday(day);
          return (
            <button
              type="button"
              key={ymd}
              className={[
                styles.dayCell,
                !isCurrentMonth || !isMarked ? styles.muted : "",
                dayIsToday && !isSelected ? styles.today : "",
                isSelected ? styles.selected : "",
              ].join(" ")}
              onClick={() => isCurrentMonth && isMarked && onSelect(ymd)}
              disabled={!isCurrentMonth || !isMarked}
            >
              {day.getDate()}
              {isMarked && <span className={styles.dot} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default SubmitterCalendarPopover;
